import { CalepinageInput, DistanceKey, ModePanneau, OrientationTerrasse, PanneauOutput, Point, SysIntegrationCalepinage } from '../model/calepinage.types'
import { CalepinageGeometry } from './calepinage-geometry'
import { CalepinageMapping } from './calepinage-mapping'

interface PanelDimensions {
    /** Dimension du panneau perpendiculaire à l'azimut (axe X local). Côté plein, non foreshortené. */
    dimX: number
    /** Dimension du panneau dans la direction de l'azimut (axe Y local). Côté foreshortené par cos(inclinaison). */
    dimY: number
    /** Distance entre 2 panneaux adjacents le long de l'axe X local. */
    distX: number
    /** Distance entre 2 panneaux adjacents le long de l'axe Y local. */
    distY: number
    /** Nb de panneaux par bloc (ou par table en mode SOL) le long de X. */
    nbX: number
    /** Nb de panneaux par bloc le long de Y. */
    nbY: number
    /** Gap inséré tous les nbX panneaux le long de X (distEntreBloc, ou L2 en mode table). */
    distEntreBlocX: number
    /** Gap inséré tous les nbY panneaux le long de Y (distEntreBloc). */
    distEntreBlocY: number
    /**
     * Mode table (centrale au sol) : nombre de modules d'une table sur l'axe Y.
     * Quand défini, seules les tables COMPLÈTES sont placées — une table est une
     * structure porteuse standard, pas question d'en dessiner une tronquée en bord
     * de zone ou d'exclusion.
     */
    modulesParTableY?: number
    mode: ModePanneau
}

export class CalepinageAlgo {
    /**
     * Tolérance numérique (unités carte projetée EPSG:3857, ~sub-mm terrain) pour le
     * remplissage maximal des ombrières : marge nulle ⇒ les panneaux affleurent le bord du
     * rectangle, ce qu'un ray casting strict rejetterait pile sur la frontière. On élargit le
     * balayage de cette tolérance et on accepte un coin jusqu'à 2× cette valeur au-delà du bord,
     * pour absorber l'écart d'arrondi entre le buffer du dessin et la grille scalée de l'algo.
     */
    private static readonly OMBRIERE_EDGE_TOLERANCE = 1e-3

    /**
     * Génère la liste des panneaux pour une zone donnée. Coordonnées d'entrée et de sortie
     * dans le même référentiel (carte projetée, ex EPSG:3857).
     *
     * Algorithme :
     * 1. Bascule en repère local en rotant la zone et les exclusions de -azimut autour
     *    du centroïde de la zone — l'axe X devient le sens "longueur" des panneaux.
     * 2. Place une grille de blocs dans la bbox locale, avec les pas dimensionnés par
     *    les paramètres (taille panneau, distances inter-panneaux, distance entre blocs).
     * 3. Pour chaque panneau candidat, vérifie qu'il est dans la zone (avec marge
     *    Décalage_zone_panneau) et hors exclusions (avec marge Décalage_zone_sans_panneau).
     * 4. Re-rotate chaque panneau retenu vers le repère monde.
     */
    static generate(input: CalepinageInput): PanneauOutput[] {
        const { zonePolygon, exclusionPolygons, azimutDeg, sysIntegration, orientation, parameters } = input

        const isOmbriere = sysIntegration === 'OMBRIERE'
        const dims = CalepinageAlgo.computeDimensions(sysIntegration, orientation, parameters, input.tableMode ?? false)

        // Ombrières (retour client 11041 / 14460) : remplissage exact bord à bord, sans marge de bord
        // — VL largeur fixe ET PL/portique (la cliente a tranché : pas de marge pour PL/portique). Les
        // panneaux affleurent le tracé dessiné : on élargit très légèrement le balayage (margeBord) et
        // on tolère un coin affleurant côté inclusion (margeInclusion). Toiture/sol gardent la marge de
        // sécurité (decalageZonePanneau).
        const margeBord = isOmbriere ? -CalepinageAlgo.OMBRIERE_EDGE_TOLERANCE : parameters.decalageZonePanneau
        const margeInclusion = isOmbriere ? -2 * CalepinageAlgo.OMBRIERE_EDGE_TOLERANCE : parameters.decalageZonePanneau

        const center = CalepinageGeometry.centroid(zonePolygon)
        // Rotation pour amener la direction de la flèche d'azimut sur l'axe local +Y.
        const angleRad = CalepinageGeometry.toRadians(azimutDeg)
        const inverseAngle = -angleRad

        const localZone = CalepinageGeometry.rotatePolygon(zonePolygon, angleRad, center)
        const localExclusions = exclusionPolygons.map(e => CalepinageGeometry.rotatePolygon(e, angleRad, center))
        const bbox = CalepinageGeometry.boundingBox(localZone)

        const minX = bbox.minX + margeBord
        const minY = bbox.minY + margeBord
        const limitX = bbox.maxX - margeBord
        const limitY = bbox.maxY - margeBord

        // Recherche de la position de grille (offsetX, offsetY) qui maximise le nombre de
        // panneaux placés. On combine deux types d'offsets :
        //   1. Offsets fins dans [0, step) — décalent la grille à l'intérieur d'un bloc.
        //   2. Offsets gros pas balayant la période complète (bloc + distEntreBloc) — décalent
        //      la frontière entre blocs, ce qui peut libérer des positions au bord de la zone.
        const stepX = dims.dimX + dims.distX
        const stepY = dims.dimY + dims.distY
        const blocPeriodX = dims.nbX * dims.dimX + (dims.nbX - 1) * dims.distX + dims.distEntreBlocX
        const blocPeriodY = dims.nbY * dims.dimY + (dims.nbY - 1) * dims.distY + dims.distEntreBlocY
        const fineFractions = [0, 0.0625, 0.125, 0.1875, 0.25, 0.3125, 0.375, 0.4375, 0.5, 0.5625, 0.625, 0.6875, 0.75, 0.8125, 0.875, 0.9375]
        const offsetsX = new Set<number>()
        const offsetsY = new Set<number>()
        for (const f of fineFractions) {
            offsetsX.add(f * stepX)
            offsetsY.add(f * stepY)
        }
        // Quelques offsets supplémentaires sur la période bloc pour balayer le décalage de
        // la frontière entre blocs.
        for (const f of [0.25, 0.5, 0.75]) {
            offsetsX.add(f * blocPeriodX % stepX)
            offsetsY.add(f * blocPeriodY % stepY)
        }

        let bestCount = -1
        let bestPanneaux: PanneauOutput[] = []
        let bestCenterScore = Number.POSITIVE_INFINITY

        for (const dy of offsetsY) {
            const yPositions = CalepinageAlgo.computeAxisPositions(minY + dy, limitY, dims.dimY, dims.distY, dims.nbY, dims.distEntreBlocY)
            if (yPositions.length === 0) continue
            for (const dx of offsetsX) {
                const xPositions = CalepinageAlgo.computeAxisPositions(minX + dx, limitX, dims.dimX, dims.distX, dims.nbX, dims.distEntreBlocX)
                if (xPositions.length === 0) continue
                const candidate = CalepinageAlgo.placePanneaux(
                    xPositions,
                    yPositions,
                    dims,
                    localZone,
                    localExclusions,
                    parameters,
                    margeInclusion,
                    inverseAngle,
                    center
                )
                if (candidate.length === 0) continue

                const slackLeft = xPositions[0] - minX
                const slackRight = limitX - (xPositions[xPositions.length - 1] + dims.dimX)
                const slackBottom = yPositions[0] - minY
                const slackTop = limitY - (yPositions[yPositions.length - 1] + dims.dimY)
                const centerScore = Math.abs(slackRight - slackLeft) + Math.abs(slackTop - slackBottom)

                if (candidate.length > bestCount || (candidate.length === bestCount && centerScore < bestCenterScore)) {
                    bestCount = candidate.length
                    bestPanneaux = candidate
                    bestCenterScore = centerScore
                }
            }
        }

        return bestPanneaux
    }

    private static placePanneaux(
        xPositions: number[],
        yPositions: number[],
        dims: PanelDimensions,
        localZone: Point[],
        localExclusions: Point[][],
        parameters: { decalageZoneSansPanneau: number },
        zoneMargin: number,
        inverseAngle: number,
        center: Point
    ): PanneauOutput[] {
        const candidates: { ix: number; iy: number; localCorners: Point[] }[] = []

        for (let iy = 0; iy < yPositions.length; iy++) {
            const y0 = yPositions[iy]
            for (let ix = 0; ix < xPositions.length; ix++) {
                const x0 = xPositions[ix]
                const x1 = x0 + dims.dimX
                const y1 = y0 + dims.dimY
                const localCorners: Point[] = [
                    { x: x0, y: y0 },
                    { x: x1, y: y0 },
                    { x: x1, y: y1 },
                    { x: x0, y: y1 },
                ]

                const ok =
                    CalepinageGeometry.rectInsidePolygonWithMargin(
                        localCorners,
                        localZone,
                        zoneMargin
                    ) &&
                    CalepinageGeometry.rectAvoidsExclusionsWithMargin(
                        localCorners,
                        localExclusions,
                        parameters.decalageZoneSansPanneau
                    )

                if (ok) {
                    candidates.push({ ix, iy, localCorners })
                }
            }
        }

        const retained = CalepinageAlgo.filterTablesIncompletes(candidates, dims)

        const stamp = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
        let panelCounter = 0
        return retained.map(({ ix, iy, localCorners }) => {
            const worldCorners = CalepinageGeometry.rotatePolygon(localCorners, inverseAngle, center)
            const blocIdX = Math.floor(ix / dims.nbX)
            const blocIdY = Math.floor(iy / dims.nbY)
            return {
                id: `panel-${stamp}-${++panelCounter}`,
                blocId: blocIdY * 1000 + blocIdX,
                coordinates: worldCorners,
            }
        })
    }

    /**
     * Mode table : élimine les tables incomplètes (une table = colonne ix × groupe de
     * modulesParTableY positions Y consécutives). Une table tronquée en bord de zone ou
     * d'exclusion n'est pas constructible — on ne la dessine pas du tout.
     */
    private static filterTablesIncompletes(
        candidates: { ix: number; iy: number; localCorners: Point[] }[],
        dims: PanelDimensions
    ): { ix: number; iy: number; localCorners: Point[] }[] {
        const parTable = dims.modulesParTableY
        if (!parTable || parTable <= 1) return candidates

        const countByTable = new Map<string, number>()
        for (const c of candidates) {
            const key = `${c.ix}:${Math.floor(c.iy / parTable)}`
            countByTable.set(key, (countByTable.get(key) ?? 0) + 1)
        }
        return candidates.filter(c => countByTable.get(`${c.ix}:${Math.floor(c.iy / parTable)}`) === parTable)
    }

    /**
     * Calcule les positions de départ des panneaux le long d'un axe entre `min` et `limit`,
     * avec pas `dim+dist` entre panneaux d'un même bloc et `distEntreBloc` ajouté tous les
     * `nb` panneaux.
     */
    private static computeAxisPositions(
        min: number,
        limit: number,
        dim: number,
        dist: number,
        nb: number,
        distEntreBloc: number
    ): number[] {
        const positions: number[] = []
        let pos = min
        let i = 0
        while (pos + dim <= limit + 1e-6) {
            positions.push(pos)
            i++
            const step = dim + dist + (i % nb === 0 ? distEntreBloc : 0)
            pos += step
        }
        return positions
    }

    /**
     * Convention : axe X local = perpendiculaire à l'azimut (= en travers de la pente),
     * axe Y local = direction de l'azimut (= le long de la pente, foreshortené).
     *
     * - Paysage  : long côté du panneau perpendiculaire à la pente → longueur sur X.
     *   - dimX = longueur (plein), dimY = largeur × cos(inclinaison) (foreshortené)
     *   - le long de X on a "longueur direction" → distX = dist_long, nbX = NB_LARG_PAYSAGE
     * - Portrait : long côté du panneau le long de la pente → longueur sur Y.
     *   - dimX = largeur (plein), dimY = longueur × cos(inclinaison) (foreshortené)
     *   - le long de X on a "largeur direction" → distX = dist_larg, nbX = NB_LONG_PORTRAIT
     *
     * Mode table (centrale au sol, ticket 11037, précisé en réunion du 26/05/2026) :
     * une table = nbPanneauxLargTable modules en PORTRAIT, côte à côte dans le sens de
     * l'azimut (axe Y local) ; les lignes de tables se remplissent librement sur l'axe X
     * (le nombre de modules par ligne dépend de la forme de la zone).
     *   - Axe Y : nbY = nbPanneauxLargTable (profondeur d'une table), et L2 est un
     *     ENTRAXE (début de table → début de table) : le gap inséré entre deux tables
     *     vaut max(0, L2 − profondeur de table projetée).
     *   - Axe X : remplissage continu (gap inter-bloc nul).
     *   - Foreshortening avec l'inclinaison SOL (les tables d'une centrale au sol n'ont
     *     pas l'inclinaison des toitures).
     * La toiture/ombrière (tableMode=false) garde exactement le comportement bloc historique.
     */
    private static computeDimensions(
        sys: CalepinageInput['sysIntegration'],
        orientation: CalepinageInput['orientation'],
        p: CalepinageInput['parameters'],
        tableMode: boolean
    ): PanelDimensions {
        const mode = CalepinageMapping.modePanneau(sys)
        const cosI = Math.cos(CalepinageGeometry.toRadians(p.inclinaisonSysteme))
        const distKey = CalepinageAlgo.distanceKey(sys, orientation)
        // Ombrière (retour client 11041) : aucune notion de « bloc » → distEntreBloc = 0 pour TOUTE
        // ombrière. L'espace inter-panneaux configuré (DIST_*_PANNEAU_OMBRIERE) s'applique à TOUTES
        // les ombrières (largeur fixe comprise) ; pour la largeur fixe, la largeur dessinée intègre
        // ces (nb-1) espaces (CalepinageService.ombriereWidthMeters) afin de conserver exactement nb
        // panneaux. SOUDE (aussi PORTRAIT) conserve tout → on cible bien OMBRIERE.
        const isOmbriere = sys === 'OMBRIERE'

        if (tableMode) {
            // PORTRAIT forcé : 2 modules côte à côte dans le sens de la pente (image Valentine).
            const cosSol = Math.cos(CalepinageGeometry.toRadians(p.inclinaisonSol))
            const dimY = p.longueurPanneau * cosSol
            // Écarts inter-panneaux DÉDIÉS au sol (retour client 11041) : le SOL a ses propres
            // paramètres DIST_*_PANNEAU_SOL, distincts des écarts LESTÉ de la toiture terrasse.
            const distY = p.distLongPanneauSol
            const nbY = Math.max(1, Math.round(p.nbPanneauxLargTable))
            const profondeurTable = nbY * dimY + (nbY - 1) * distY
            const gapEntreTables = Math.max(0, p.distanceEntreTableM - profondeurTable)
            return {
                dimX: p.largeurPanneau,
                dimY,
                distX: p.distLargPanneauSol,
                distY,
                nbX: p.nbPanneauxLongBlocPortrait,
                nbY,
                distEntreBlocX: 0,
                distEntreBlocY: gapEntreTables,
                modulesParTableY: nbY,
                mode: 'PORTRAIT',
            }
        }

        if (mode === 'PAYSAGE') {
            return {
                dimX: p.longueurPanneau,
                dimY: p.largeurPanneau * cosI,
                distX: p.distLongPanneau[distKey],
                distY: p.distLargPanneau[distKey],
                nbX: p.nbPanneauxLargBlocPaysage,
                nbY: p.nbPanneauxLongBlocPaysage,
                distEntreBlocX: p.distEntreBloc,
                distEntreBlocY: p.distEntreBloc,
                mode,
            }
        }
        return {
            dimX: p.largeurPanneau,
            dimY: p.longueurPanneau * cosI,
            distX: p.distLargPanneau[distKey],
            distY: p.distLongPanneau[distKey],
            nbX: p.nbPanneauxLongBlocPortrait,
            nbY: p.nbPanneauxLargBlocPortrait,
            distEntreBlocX: isOmbriere ? 0 : p.distEntreBloc,
            distEntreBlocY: isOmbriere ? 0 : p.distEntreBloc,
            mode,
        }
    }

    /**
     * Construit la clé d'accès aux distances inter-panneaux.
     * - BAC_ACIER / OMBRIERE : pas d'orientation, clé = système.
     * - SOUDE / LESTE (toiture terrasse) : clé = `${système}_${orientation}` où
     *   l'orientation EST_OUEST est compactée en ESTOUEST pour coller au nommage back.
     *   Défaut SUD si l'orientation n'est pas fournie (hors toiture terrasse).
     */
    private static distanceKey(sys: SysIntegrationCalepinage, orientation: OrientationTerrasse | null): DistanceKey {
        if (sys === 'BAC_ACIER' || sys === 'OMBRIERE') return sys
        const suffix = orientation === 'EST_OUEST' ? 'ESTOUEST' : 'SUD'
        return `${sys}_${suffix}` as DistanceKey
    }
}
